import React, { CSSProperties, useEffect, useState } from 'react';
import { DragLayerMonitor, useDragDropManager, useDragLayer, XYCoord } from 'react-dnd';
import { TimeslotDragType } from '../../../../models/calendar';
import { Unsubscribe } from 'dnd-core/dist/interfaces';
import { Timeslot } from '../Timeslot';
import { StartOfWeekFormatSetting, TimeFormatSetting, TimeslotDay } from '../../../../models/graphql';
import { Property } from 'csstype';
import { PostingPlanHelpers } from '../postingPlanHelpers';
import scssVars from '../../../../assets/scss/exportedVars.module.scss';

type TargetType = {
  hour: number;
  height: number;
  y: number;
  x: number;
};

type TimeAndOffsetType = {
  time: string;
  offset: XYCoord;
  isOverTarget: boolean;
};

export type TimeslotDragLayerViewProps = {
  //
  timeFormat: TimeFormatSetting;
  startDayOfWeek: StartOfWeekFormatSetting;
};

const getStyles = (item: TimeslotDragType | null, offset: XYCoord, isOver: boolean): CSSProperties => {
  let position: Property.Position = 'fixed';
  let transform: Property.Transform = `translate3d(0, 0, 0)`;
  let display: Property.Display = 'none';
  const width: Property.Width = item?.width ? `${item.width}px` : '0';
  if (item) {
    position = 'fixed';
    transform = `translate3d(${offset.x}px, ${offset.y}px, 0)`;
    display = isOver ? 'block' : 'none';
  }
  return {
    cursor: isOver ? 'move' : 'not-allowed',
    display,
    left: 0,
    margin: 8,
    pointerEvents: 'none',
    position,
    top: 0,
    transform,
    WebkitTransform: transform,
    width,
    zIndex: scssVars['z-index-preloader']
  };
};

export const TimeslotDragLayerView = (props: TimeslotDragLayerViewProps): JSX.Element => {
  const dragDropManager = useDragDropManager();
  const monitor = dragDropManager.getMonitor();

  const { item, isDragging } = useDragLayer((monitor: DragLayerMonitor) => ({
    item: monitor.getItem() as TimeslotDragType,
    itemType: monitor.getItemType(),
    isDragging: monitor.isDragging(),
    initialOffset: monitor.getInitialClientOffset()
  }));
  const [timeAndOffset, setTimeAndOffset] = useState<TimeAndOffsetType>({
    time: item?.time || '00:00',
    offset: { x: 0, y: 0 },
    isOverTarget: false
  });
  const [day, setDay] = useState<TimeslotDay>(TimeslotDay.MONDAY);

  useEffect(() => {
    let target: TargetType;
    let unsubscribe: Unsubscribe | undefined;
    if (isDragging) {
      unsubscribe = monitor.subscribeToOffsetChange(() => {
        const targetIds = monitor.getTargetIds();
        const targetId = targetIds.length ? (targetIds[0] as string) : '';
        const targetReg = targetId ? dragDropManager.getRegistry().getTarget(targetId) : null;
        const targetEl = document.getElementById(targetId);
        if (targetReg && targetEl) {
          const dayStr = targetEl.getAttribute('data-day') as string;
          const targetDay = +dayStr;
          const hourStr = targetEl.getAttribute('data-hour') as string;
          const hour = +hourStr;
          const { height, y, x } = targetEl.getBoundingClientRect();
          target = { hour, height, y, x };
          const currentDay = PostingPlanHelpers.getDayFromColumn(targetDay, props.startDayOfWeek);
          if (day !== currentDay) {
            setDay(currentDay);
          }
        } else {
          setTimeAndOffset(old => ({ ...old, isOverTarget: false }));
          return;
        }
        const clientOffset = monitor.getClientOffset();
        if (clientOffset) {
          const { y } = clientOffset as XYCoord;
          const dy = y - target.y;
          const step = Math.round(target.height / 12);
          const minutes = Math.floor(dy / step) * 5;
          const steps = Math.floor(dy / step);
          setTimeAndOffset(old => {
            const newTimeOffset = {
              ...old
            };
            if (old.time !== `${target.hour}:${minutes}`) {
              newTimeOffset.time = `${target.hour}:${minutes}`;
            }
            newTimeOffset.offset = {
              x: target.x,
              y: target.y + steps * step
            };
            newTimeOffset.isOverTarget = true;
            return newTimeOffset;
          });
        }
      });
    }
    return unsubscribe;
  }, [monitor, isDragging, props.startDayOfWeek, day, dragDropManager]);

  return (
    <div style={getStyles(item, timeAndOffset.offset, timeAndOffset.isOverTarget)}>
      <Timeslot
        timeFormat={props.timeFormat}
        isListView={false}
        isDragging={true}
        socialProfiles={item?.socialProfiles || []}
        name={item?.name || ''}
        colour={item?.colour || null}
        categoryId={''}
        actions={{
          edit: () => {
            /**/
          },
          delete: () => {
            /**/
          },
          clone: () => {
            /**/
          }
        }}
        onboardingContent={item?.onboardingContent || false}
        id={item?.id || ''}
        day={day}
        hovering={false}
        time={timeAndOffset.time}
      />
    </div>
  );
};
